/* eslint-disable max-lines-per-function, max-params, max-lines */
import { api, toQueryString } from "jiffy-api";
import getVariantInfo from "common/jiffy/get-variant";
import { sendDataLayer, track } from "jiffy-analytics";
import { clearCachedEstimates } from "components/jiffy/product-details/cache-composition-delivery-date";
import idleCallback from "../../../common/utils/ui/idle_callback";

// Getters
const getIsBuyItAgainStatus = () => !!document.getElementById("js-buy-it-again-modal");
const $getCartName = () => document.querySelector(".js-continue-shopping-modal-cart-name");
const $getCutOff = () => document.querySelector(".js-product-details-delivery-dates-cutoff");

export const sendFacebookConversionApiData = async (
  content,
  selectedColor,
  viewContentEventId,
  pageViewEventId,
  eventName
) => {
  const queryString = toQueryString({ ac: selectedColor });
  await api.product.sendFacebookConversionApi({
    content,
    page_view_event_id: pageViewEventId,
    view_content_event_id: viewContentEventId,
    event_name: eventName,
    source_url: window.location.href.replace(/\?.*/, `?${queryString}`)
  });
};

const sendPdpVisit = async (variantInputs, variantId, skus, selectedColor) => {
  const cartFirstVariant =
    variantInputs.find(item => item.dataset.isStandardSize) || variantInputs[0];
  const variant = await getVariantInfo(variantId);
  const fbData = JSON.parse(variant.fbData);
  const viewContentEventId = `VC-${fbData.external_id[0]}-${Date.now()}`;
  const pageViewEventId = `PV-${fbData.external_id[0]}-${Date.now()}`;
  const fbContent = {
    content_ids: skus,
    content_category: variant.product.adwords_category,
    content_name: variant.product.name,
    content_type: "product",
    user_data: fbData
  };

  const {
    catalogNumber: firstCatalogNumber,
    color: firstColor,
    detailedProductName: firstDetailedProductName,
    brandName: firstBrandName,
    price: firstPrice
  } = cartFirstVariant.dataset;
  const itemsViewed = [
    {
      id: `${firstCatalogNumber} ${firstColor}`,
      name: firstDetailedProductName,
      brand: firstBrandName,
      price: firstPrice,
      google_business_vertical: "retail"
    }
  ];
  const viewItemValue = firstPrice;

  const currentItem = variantInputs[0];
  let ecommerceData;
  if (currentItem) {
    const {
      catalogNumber,
      color,
      detailedProductName,
      brandName,
      googleAdsCategory,
      price,
      currency
    } = currentItem.dataset;
    ecommerceData = {
      ecommerce: {
        currency,
        value: Number(price),
        items: [
          {
            item_id: `${catalogNumber} ${color}`,
            item_name: detailedProductName,
            item_brand: brandName,
            item_category: googleAdsCategory,
            price
          }
        ]
      }
    };
  } else {
    ecommerceData = {};
  }

  sendFacebookConversionApiData(
    fbContent,
    selectedColor,
    viewContentEventId,
    pageViewEventId,
    "ViewContent"
  );

  sendDataLayer({
    event: "PDPPageVisit",
    items: variantInputs.map(item => ({ product_id: item.dataset.sku })),
    msAdProductIds: skus,
    itemsViewed,
    viewItemValue,
    fbContent,
    viewContentEventId,
    pageViewEventId,
    ...ecommerceData
  });
};

let productAddedModalData = {};
export const addToCartAnalytics = async target => {
  sendDataLayer({
    genericEvent: true,
    event: "click",
    eventCategory: "UX",
    eventLabel: "add to cart"
  });

  const itemQuantities = Object.entries(target.itemsQty);
  const cartFirstVariantId = itemQuantities[0][0];
  const cartFirstVariant = await getVariantInfo(cartFirstVariantId);
  const cartSkus = [];
  const cartItems = [];
  let addToCartValue = 0;
  const itemsAdded = [];
  const ecommerceItemsAdded = [];
  const itemAddedMixpanel = [];
  productAddedModalData = {};
  let variantInfo;
  let item;
  itemQuantities.forEach(async ([variantId, quantity]) => {
    variantInfo = await getVariantInfo(variantId);
    cartSkus.push(variantInfo.sku);
    cartItems.push({ product_id: variantInfo.sku });
    addToCartValue += quantity * variantInfo.price;
    item = {
      id: variantInfo.product.item_group_id,
      name: variantInfo.product.name,
      brand: variantInfo.product.brand,
      variant: variantInfo.color,
      price: variantInfo.price,
      currency: variantInfo.currency,
      size: variantInfo.size
    };
    itemsAdded.push({
      id: item.id,
      name: item.name,
      brand: item.brand,
      variant: item.color,
      price: item.price,
      quantity
    });
    ecommerceItemsAdded.push({
      item_id: item.id,
      item_name: item.name,
      item_brand: item.brand,
      item_variant: item.variant,
      price: item.price,
      currency: item.currency,
      size: item.size,
      quantity
    });
    itemAddedMixpanel.push({
      quantity,
      sku: variantInfo.sku,
      spree_composition_id: variantInfo.compositionId,
      composition: variantInfo.color,
      favorite: variantInfo.favorite,
      fulfillment_group: variantInfo.fulfillmentGroup
    });

    const trackProperties = {
      brand: variantInfo.product.brand,
      product_category: variantInfo.product.adwords_category,
      product: variantInfo.product.name,
      spree_composition_id: variantInfo.compositionId,
      composition: variantInfo.color,
      fulfillment_group: variantInfo.fulfillmentGroup || "",
      sku: variantInfo.sku,
      quantity,
      buy_it_again: getIsBuyItAgainStatus(),
      favorite: variantInfo.favorite,
      softness_score: variantInfo.softnessScore,
      business_line: variantInfo.product.business_line
    };

    if (target.dtfSizes) {
      trackProperties.dtf_width = target.dtfSizes.width;
      trackProperties.dtf_height = target.dtfSizes.height;
      trackProperties.dtf_area = target.dtfSizes.area;
    }

    track("product_added", trackProperties);
  });

  const $container = document.querySelector("#product-page-container");
  const { orderEmail: email } = $container ? $container.dataset : {};
  const { brand, name, adwordsCategory, color, compositionId } = cartFirstVariant.product;

  addToCartValue = Number(addToCartValue.toFixed(2));
  const fbContent = {
    content_name: cartFirstVariant.product.name,
    content_category: cartFirstVariant.product.adwords_category,
    content_ids: cartSkus,
    content_type: "product",
    user_data: JSON.parse(cartFirstVariant.fbData)
  };
  const { eventId: fbAddToCartEventId } = target;
  sendDataLayer({
    event: "AddToCart",
    cartItems,
    itemsAdded,
    addToCartValue,
    email,
    fbContent,
    addToCartEventId: fbAddToCartEventId,
    ecommerce: {
      value: addToCartValue,
      items: ecommerceItemsAdded
    }
  });

  if (target.isBulk) {
    track("product_added_bulk_form", {
      brand: cartFirstVariant.product.brand,
      product: cartFirstVariant.product.name,
      product_category: cartFirstVariant.product.adwords_category,
      items: itemAddedMixpanel,
      business_line: cartFirstVariant.product.business_line
    });
  }

  productAddedModalData = {
    brand,
    product: name,
    product_category: adwordsCategory,
    composition_id: compositionId,
    composition: color,
    items_added: itemAddedMixpanel,
    items_value: addToCartValue,
    business_line: cartFirstVariant.product.business_line
  };

  clearCachedEstimates();
};

export const triggerPdpVisitEvent = (activeColor = "") => {
  const variantId = $(`#product-details-variants-form input[data-color="${activeColor}"]`)
    .first()
    .data("variant-id");

  const variantInputs = Array.from(
    document.querySelectorAll(`#product-details-variants-form input[data-color="${activeColor}"]`)
  );
  const skus = variantInputs.map(item => item.dataset.sku);

  if (skus.length) {
    idleCallback(() => {
      sendPdpVisit(variantInputs, variantId, skus, activeColor);
    });
  }

  return variantId;
};

export const trackProductAddedModal = (action, trackingAdditionalProps) => {
  const props = {
    ...productAddedModalData,
    ...trackingAdditionalProps,
    action,
    cart_name: $getCartName().textContent,
    cart_delivery_date_valid: $getCutOff().textContent
  };
  track("product_added_modal", props);
};
